import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ReportingService } from '../services/reporting/reporting.service';
import { DayReport } from '../models/dayreport';
import { FormControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { PrepareListData } from '../helpers/preplistdata';
import { ReportSummaries } from '../models/reportsummaries';
import { ProjectService } from '../services/project/project.service';
import { Project } from '../models/project';
import { ReturnObject } from '../models/returnObject';
import { InternalMsgService } from '../services/internal.msg.service';
import { AccountService } from '../services/account.service';
import { AccountItem } from '../models/accountitem';
import { Sort } from '../models/sort';
import { StatusMessagesService } from '../services/statusmessage.service';

@Component({
    selector: 'groupprojectreport',
    templateUrl: './groupprojectreport.component.html',
    styleUrls: ['./groupprojectreport.component.css'],
    providers: [],
})
export class GroupProjectReportComponent implements OnInit, OnDestroy {
    showSpinner = false;
    sortField = 'month';
    selectedProject = '';

    loggedAccount: AccountItem = new AccountItem;
    dataSource: DayReport[] = [];
    summary: ReportSummaries = new ReportSummaries();
    fromDate: string = '';
    toDate: string = '';
    minDate = new Date(2017, 0, 1);
    maxDate = new Date(new Date().getFullYear() + 1, 11, 31);
    projectList: Project[] = [];
    filteredProjects: Observable<Project[]> | undefined;
    msgSubscr: Subscription = new Subscription;
    spinnerMessage: string = 'Please wait, report data is fetched';
    reportTotalHours: number = 0;
    rowCount: number = 0;

    constructor(private reportingService: ReportingService,
                private projectService: ProjectService,
                private msgService: InternalMsgService,
                private statusMessagesService: StatusMessagesService,
                private accountService: AccountService) {
    }


    // ----------------------------------------------------------------------------------
    // -- Life cycle hooks
    // ----------------------------------------------------------------------------------
    ngOnInit() {
        const today = new Date();
        this.fromDate = today.toISOString().toString().split('T')[0];
        this.toDate = today.toISOString().split('T')[0];

        window.addEventListener("tdsSort", (event: any) => {
            const details = event['detail'];
            const sortObj: Sort = {
                active: details['columnKey'],
                direction: details['sortingDirection']
            }
            this.sort(sortObj);
        });

        this.msgSubscr = this.msgService.loggedInAccountSet$.subscribe( 
            account => {
                this.loggedAccount = account;
                this.getProjectList();
        } );     
        this.accountService.fetchAccount();
    }

    ngOnDestroy() {
        this.msgSubscr.unsubscribe();
    }

    // ----------------------------------------------------------------------------------
    // -- HTTP --------------------------------------------------------------------------
    // ----------------------------------------------------------------------------------
    getReportData() : void {
        this.msgService.ontoggleSpinnerSet(true);
        const email = this.loggedAccount.email;

        if (this.loggedAccount.reporting) {
            this.reportingService.getProjectReportData(this.selectedProject, this.fromDate, this.toDate)
                .subscribe( { 
                    next: (data: ReturnObject<DayReport[]>) => {
                        if (data.isFailure) {
                            this.statusMessagesService.showToasterError('Error', data.failureMessage);
                            return;
                        }
                        if (data.value.length === 0)  {
                            this.dataSource = data.value;
                            return;
                        }
                        PrepareListData.prepareData(data.value, this.summary, this.sortField).then( (orderedData) => {
                            this.dataSource = orderedData;
                        });
                        this.countHours();

                    },
                    error: (err) => {         
                        this.statusMessagesService.showHttpError(err);
                    },
                    complete: () => {         
                        this.msgService.ontoggleSpinnerSet(false);
                    }
                }
            );
        } else {
            this.reportingService.getProjectReportDataForEmp(this.selectedProject, email, this.fromDate, this.toDate)
                .subscribe( { 
                    next: (data: ReturnObject<DayReport[]>) => {
                        if (data.isFailure) {
                            this.statusMessagesService.showToasterError('Error', data.failureMessage);
                            return;
                        }
                        if (data.value.length === 0)  {
                            this.dataSource = data.value;
                            return;
                        }
                        PrepareListData.prepareData(data.value, this.summary, this.sortField).then( (orderedData) => {
                            this.dataSource = orderedData;
                        });
                        this.countHours();

                    },
                    error: (err) => {         
                        this.statusMessagesService.showHttpError(err);
                    },
                    complete: () => {         
                        this.msgService.ontoggleSpinnerSet(false);
                    }
                }
            );
        }
    }

    getProjectList() : void {
        this.msgService.ontoggleSpinnerSet(true);

        this.projectService.getAllProjects()
            .subscribe( { 
                next: (data: ReturnObject<Project[]>) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    this.projectList = data.value;
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }


    // ----------------------------------------------------------------------------------
    // -- LOGIC
    // ----------------------------------------------------------------------------------
    selectProject(project: Project, emplolyeeListCtrl: FormControl): void {
        this.selectedProject = `${project.projNo} - ${project.projName}`;
    }

    filterProjects(searchText: string): Project[] {
        return this.projectList.filter(project => {
            if (typeof searchText !== 'string') {
                return false;
            }
            const input = searchText.toLowerCase();
            return project.projName !== null
                ? project.projNo.toLowerCase().indexOf(input) >= 0 || project.projName.toLowerCase().indexOf(input) >= 0
                : false;
        });
    }

    async sort(sort: Sort) {
        if (this.dataSource.length === 0) return;
        this.showSpinner = true;
        PrepareListData.sort(this.dataSource, sort, this.sortField ).then( data => {
            this.dataSource = data; 
            this.showSpinner = false;
        });
    }
    countHours(): void {
        this.reportTotalHours = 0;
        if (this.dataSource && this.dataSource.length > 0) {
            for (let days of this.dataSource) {
                if (days.isSubTotalRow) {
                    this.reportTotalHours += days.hours;
                }
            }
        }
    }
    setSelectedProject(project: Project): void {
        this.selectedProject = project.projNo;
    }

    isSubmitDisabled(): boolean {
        if (this.fromDate == '' || this.toDate == '') return true;

        const dtFrom: Date = new Date(this.fromDate);
        const dtTo: Date = new Date(this.toDate);
        return this.selectedProject.trim() === ''
        || (dtTo < dtFrom);
    }

}
